/* eslint-disable no-param-reassign,no-unused-vars */

import { each, find, get } from 'lodash';
import moment from 'moment';
import { DATA_LOADING_STATES, METRIC_NAMES } from '@config/Consts';
import { RESOURCES as AGENT_MANAGER_RESOURCES } from '@core/clients/AgentManagerClient';
import { RESOURCES as REPORTING_RESOURCES } from '@core/clients/ReportingClient';

const skillMetricsTemplate = () => {
  const skillTemplate = {
    id: null,
    metrics: [
      { metric: METRIC_NAMES.SKILL_ASSIGNED_CONVERSATIONS,
        score: null,
      },
      { metric: METRIC_NAMES.SKILL_LOAD,
        score: null,
      },
      { metric: METRIC_NAMES.SKILL_ONLINE_AGENTS,
        score: null,
      },
      { metric: METRIC_NAMES.SKILL_AWAY_AGENTS,
        score: null,
      },
      { metric: METRIC_NAMES.SKILL_BACK_SOON_AGENTS,
        score: null,
      },
      { metric: METRIC_NAMES.SKILL_CLOSED_CONVERSATIONS,
        score: null,
      },
      { metric: METRIC_NAMES.AVG_WAIT_TIME,
        score: null,
      },
      { metric: METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE,
        score: null,
      },
      { metric: METRIC_NAMES.AVG_TIME_TO_RESPONSE,
        score: null,
      },
      { metric: METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT,
        score: null,
      },
      { metric: METRIC_NAMES.OVERDUE_TOTAL,
        score: null,
      },
      { metric: METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS,
        score: null,
      },
      { metric: METRIC_NAMES.OVERDUE_IN_QUEUE,
        score: null,
      },
      { metric: METRIC_NAMES.SKILL_WAIT_TIME_AGENT_ASSIGNMENT_50_PERCENTILE,
        score: null,
      },
      { metric: METRIC_NAMES.SKILL_WAIT_TIME_AGENT_ASSIGNMENT_90_PERCENTILE,
        score: null,
      },
      { metric: METRIC_NAMES.OVERDUE_ASSIGNED,
        score: null,
      },
      { metric: METRIC_NAMES.ACTIONABLE,
        score: null,
      },
      // calculated metric (not from api) assigned + unassigned
      { metric: METRIC_NAMES.SKILL_OPEN_CONVERSATIONS,
        score: 0,
      },
      // calculated metric (not from api) hopefully returned from metrics api
      { metric: METRIC_NAMES.SKILLS_SKILL_NAME,
        score: '',
      },
      { metric: METRIC_NAMES.CONVERSATION_IN_QUEUE_WAIT_TIME,
        score: '',
      },
    ],
  };
  return skillTemplate;
};

const setPrimarySkillsMetrics = (state, skillsMetrics) => {
  state.skillsMetrics = [];
  each(skillsMetrics, (skillMetrics) => {
    const skill = skillMetricsTemplate();
    skill.id = skillMetrics.id;
    each(skillMetrics.metrics, (metric) => {
      const metricData = find(skill.metrics, m => m.metric === metric.metric);
      if (metricData) {
        metricData.score = metric.score;
      }
    });
    each(Object.keys(state.lastResponses), (key) => {
      const skillMetric = find(state.lastResponses[key], skillData =>
        skillData.id === skill.id);
      if (skillMetric) {
        each(skillMetric.metrics, (metric) => {
          const metricData = find(skill.metrics, m => m.metric === metric.metric);
          if (metricData) {
            metricData.score = metric.score;
          }
        });
      }
    });
    // calculate open conversations using assigned + unassigned
    const openMetric = find(skill.metrics, metricData =>
      metricData.metric === METRIC_NAMES.SKILL_OPEN_CONVERSATIONS);
    const assignedMetric = find(skill.metrics, metricData =>
      metricData.metric === METRIC_NAMES.SKILL_ASSIGNED_CONVERSATIONS);
    const unassignedMetric = find(skill.metrics, metricData =>
      metricData.metric === METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS);
    openMetric.score = assignedMetric.score + unassignedMetric.score;
    state.skillsMetrics.push(skill);
  });
};

const setResourceDataStatus = (state, data) => {
  const dataStatus = get(data, 'dataStatus');
  state.dataStatus[data.resourceName] = dataStatus;
  if (dataStatus === DATA_LOADING_STATES.SUCCESS) {
    state.lastTimeDataUpdated[data.resourceName] = moment();
  }
};

const setDataStatus = (state, dataStatus) => {
  state.dataStatus[AGENT_MANAGER_RESOURCES.METRICS] = dataStatus;
  state.dataStatus[REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH] = dataStatus;
};

const isPrimaryResource = resource => resource === AGENT_MANAGER_RESOURCES.METRICS;

const updateSkillsMetrics = (state, data) => {
  setResourceDataStatus(state, { resourceName: data.resourceName, dataStatus: data.dataStatus });
  if (data.dataStatus === DATA_LOADING_STATES.SUCCESS) {
    state.lastTimeDataUpdated[data.resourceName] = moment();
    if (isPrimaryResource(data.resourceName)) {
      setPrimarySkillsMetrics(state, data.skillsMetrics);
    } else {
      state.lastResponses[data.resourceName] = data.skillsMetrics;
      if (state.skillsMetrics && state.skillsMetrics.length > 0) {
        const newSkillsMetrics = [...state.skillsMetrics];
        each(data.skillsMetrics, (skillMetric) => {
          const index = newSkillsMetrics.findIndex(skill => skill.id === skillMetric.id);
          const currentSkill = newSkillsMetrics[index];
          if (currentSkill) {
            each(skillMetric.metrics, (metric) => {
              const metricDataIndex =
                currentSkill.metrics.findIndex(m => m.metric === metric.metric);
              const metricData = currentSkill.metrics[metricDataIndex];
              if (metricData && (metricData.score !== metric.score)) {
                metricData.score = metric.score;
              }
            });
          }
        });
        state.skillsMetrics = newSkillsMetrics;
      }
    }
  }
};

const setSort = (state, sort) => {
  state.sort = sort;
};

const setCurrentPageSkillIds = (state, currentPageSkillIds) => {
  state.currentPageSkillIds = currentPageSkillIds;
};

const setPresetValues = (state, preset) => {
  setSort(state, preset.sort);
};

export default {
  updateSkillsMetrics,
  setDataStatus,
  setResourceDataStatus,
  setSort,
  setCurrentPageSkillIds,
  skillMetricsTemplate, // just for UT purpose
  setPresetValues,
};
